import React, { useState, useEffect } from "react";
import {
    Layout,
    Space
} from "antd";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import BalanceHeaderV3 from "../../../components/header/balanceHeaderV3";
import ConnectedAccounts from "./connectedAccounts";
import BankExposure from "./bankExposure";
import { backendGetRequest, backendGetRequestWithCache } from "../../../helpers/callBackend";
import { documentTitle } from "../../../components/header/documentTitle";
import FormattedTabs from "../../../components/generic/formattedTabs";
import { amountStrtoFloat } from "../../../helpers/stringFormat";
import ClosedAccounts from "./closedAccounts";
import OuterBox from "../../../components/generic/arpariBoxes/OuterBox";


function MyAccountsIndex() {
    const [loadingAccountsQuick, setLoadingAccountsQuick] = useState(true);
    const [accountsPerPage, setAccountsPerPage] = useState([])
    const [allAccounts, setAllAccounts] = useState([])
    const [accountsRefreshComplete, setAccountsRefreshComplete] = useState(false);
    // connected accounts
    const [connectedAccounts, setConnectedAccounts] = useState([]);
    const [pageCountReceivedQuick, setPageCountReceivedQuick] = useState(-1);
    const [pageCountExpectedQuick, setPageCountExpectedQuick] = useState(0);
    const [pageCountReceivedSlow, setPageCountReceivedSlow] = useState(-1);
    const [pageCountExpectedSlow, setPageCountExpectedSlow] = useState(0);
    // closed accounts
    const [closedAccounts, setClosedAccounts] = useState([]);
    // for bank account -> entity relation
    const [entities, setEntities] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [loadingAccountsSlow, setLoadingAccountsSlow] = useState(false);


    useEffect(() => {
        documentTitle();
    }, [])

    useEffect(() => {
        getConnectedAccounts(true);
        getEntities();
    }, []);

    useEffect(() => {
        if (!loadingAccountsQuick && !accountsRefreshComplete) {
            setAccountsRefreshComplete(true);
            getConnectedAccounts(false);
        }
    }, [loadingAccountsQuick]);



    useEffect(() => {
        if (accountsPerPage && typeof accountsPerPage === 'object') {
            const flattenedAccounts = Object.values(accountsPerPage).flat();
            setAllAccounts(flattenedAccounts);
        }
    }, [accountsPerPage]);

    useEffect(() => {
        const byInstitution = connectedAccounts.reduce((result, account) => {
            const key = account.account.bank?.name;
            if (!result[key]) {
                result[key] = [];
            }
            result[key].push(account);
            return result;
        }, {});
        let institutions = Object.entries(byInstitution).map(([name, accounts]) => {
            const entities = accounts.reduce((result, account) => {
                if (!result[account.beneficiary.name]) {
                    result[account.beneficiary.name] = {
                        key: `${account.id}-${account.beneficiary.id}`,
                        entityName: account.beneficiary.name,
                        balance: 0,
                        bankAccounts: byInstitution[name].filter(bankAccount => bankAccount.beneficiary.name === account.beneficiary.name),
                    };
                }
                result[account.beneficiary.name].balance += amountStrtoFloat(account.account.current_balance);
                return result;
            }, {});
            const allEntities = Object.values(entities);
            const entitiesOver250k = allEntities.filter(entity => entity.balance > 250000);
            return {
                institutionName: name,
                balance: accounts.reduce((total, account) => total + amountStrtoFloat(account.account.current_balance), 0),
                entities: allEntities,
                entitiesOver250k: entitiesOver250k,
            };
        });
        institutions = institutions.map(institution => {
            return {
                key: institution.institutionName,
                ...institution,
                entitiesCount: institution.entities.length,
                entitiesOver250kCount: institution.entitiesOver250k.length,
            }
        });
        setInstitutions(institutions);
    }, [connectedAccounts]);

    useEffect(() => {
        setLoadingAccountsQuick(pageCountExpectedQuick > pageCountReceivedQuick);
    }, [pageCountExpectedQuick, pageCountReceivedQuick]);

    useEffect(() => {
        setLoadingAccountsSlow(pageCountExpectedSlow > pageCountReceivedSlow);
    }, [pageCountExpectedSlow, pageCountReceivedSlow]);

    useEffect(() => {
        setConnectedAccounts(() => allAccounts.filter((bankAccount) => !(bankAccount?.account?.marked_closed)));
        setClosedAccounts(() => allAccounts.filter((bankAccount) => bankAccount?.account?.marked_closed));
    }, [allAccounts]);

    const getConnectedAccounts = (quickGet) => {
        const connectedAccountsURL = "/api/bank-account/";
        let params = { page_index: 0 };

        backendGetRequest(connectedAccountsURL, { params: params }, "")
            .then((response) => {
                if (quickGet) {
                    setPageCountExpectedQuick(response?.data?.total_pages);
                } else {
                    setPageCountExpectedSlow(response?.data?.total_pages)
                }

                for (let i = 1; i <= response?.data?.total_pages; i++) {
                    let params = { page_index: i, quick_get: quickGet };

                    backendGetRequestWithCache(
                        connectedAccountsURL,
                        { params: params },
                        (cachedResponse) => {
                            if (cachedResponse?.data) {
                                setAccountsPerPage((prevState) => {
                                    if (!prevState[params.page_index]) {
                                        return {
                                            ...prevState,
                                            [params.page_index]: [...(cachedResponse?.data?.data || [])],
                                        };
                                    }
                                    return prevState;
                                });
                            }

                        },
                        (nonCachedResponse) => {
                            if (nonCachedResponse?.data) {
                                setAccountsPerPage((prevState) => ({
                                    ...prevState,
                                    [params.page_index]: [...(nonCachedResponse?.data?.data || [])],
                                }));
                                if (quickGet) {
                                    setPageCountReceivedQuick((prevState) => (prevState < 0 ? 0 : prevState) + 1);
                                } else {
                                    setPageCountReceivedSlow((prevState) => (prevState < 0 ? 0 : prevState) + 1);
                                }
                            }
                        },
                        () => {
                            if (quickGet) {
                                setPageCountExpectedQuick((prevState) => prevState - 1)
                            } else {
                                setPageCountExpectedSlow((prevState) => prevState - 1)
                            }
                        }
                    );
                }
            })
            .catch((error) => {
                console.error("Error fetching initial page data:", error);
            });
    };


    const getEntities = async () => {
        try {
            const res = await backendGetRequest("/api/entity", { params: { 'page_index': 0, quick: false } }, "");
            for (let i = 1; i <= res?.data?.total_pages; i++) {
                try {
                    const resPage = await backendGetRequest("/api/entity", { params: { 'page_index': i, quick: false } }, "");
                    setEntities(prevState => ([...prevState, ...(resPage?.data?.entities || [])]));
                } catch (err) {
                    console.error(err);
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    const editBankAccount = (accountId, newDescription) => {
        setConnectedAccounts((prevState) => {
            const updatedConnectedAccounts = prevState.map((account) => {
                if (account.id === accountId) {
                    return {
                        ...account,
                        description: newDescription,
                    }
                }
                return account;
            });
            return updatedConnectedAccounts;
        });
    };

    const reassignBankAccount = (updatedBankAccount) => {
        setConnectedAccounts((prevState) => {
            const updatedConnectedAccounts = prevState.map((account) => {
                if (account.id === updatedBankAccount.id) {
                    return { ...updatedBankAccount }
                }
                return account;
            });
            return updatedConnectedAccounts;
        });
    }

    const deleteBankAccount = (bankAccountId) => {
        setConnectedAccounts((prevState) => {
            return prevState.filter(account => account.id !== bankAccountId);
        });
    }

    const assignEntityToBankAccounts = (entityId, bankAccountIds) => {
        const entity = entities.find(e => e.id === entityId);
        const bankAccounts = connectedAccounts.filter(bankAccount => bankAccountIds.includes(bankAccount.id));
        for (let i = 0; i < bankAccounts.length; i++) {
            bankAccounts[i].beneficiary.id = entity.id;
            bankAccounts[i].beneficiary.name = entity.entity_info.name;
            bankAccounts[i].beneficiary.address = entity.entity_info.address_one_liner;
        }
        setConnectedAccounts(prevState => {
            return prevState.map(account => {
                const updatedAccount = bankAccounts.find(updated => updated.id === account.id);
                return updatedAccount ? updatedAccount : account;
            });
        });
    }

    const unassignEntityFromBankAccounts = (bankAccountIds) => {
        const bankAccounts = connectedAccounts.filter(bankAccount => bankAccountIds.includes(bankAccount.id));
        for (let i = 0; i < bankAccounts.length; i++) {
            bankAccounts[i].beneficiary.id = "N/A";
            bankAccounts[i].beneficiary.name = "Unknown";
            bankAccounts[i].beneficiary.address = "Unknown";
        }
        setConnectedAccounts(prevState => {
            return prevState.map(account => {
                const updatedAccount = bankAccounts.find(updated => updated.id === account.id);
                return updatedAccount ? updatedAccount : account;
            });
        });
    }

    return (
        <OuterBox>
            <Grid item xs={8}>
                <Box
                    sx={{
                        margin: (theme) => theme.spacing(0),
                        backgroundColor: "white",
                        border: (theme) => `1px solid ${theme.palette.secondary.light}`,
                        borderRadius: 1,
                        padding: (theme) => theme.spacing(1),
                        minHeight: "100px",
                        width: "100%",
                        height: "100%"
                    }}
                >
                    <Typography variant="overline" fontSize={12}>All Bank Accounts</Typography>
                    <Divider />
                    <BalanceHeaderV3
                        connectedAccounts={connectedAccounts}
                        isComplete={{ total: true }}
                    />
                </Box>
            </Grid>

            <FormattedTabs
                defaultActiveKey={0}
                tabPanes={[
                    {
                        "title": "Connected Accounts",
                        "key": 0,
                        "body": <ConnectedAccounts
                            connectedAccounts={connectedAccounts}
                            setClosedAccounts={setClosedAccounts}
                            setConnectedAccounts={setConnectedAccounts}
                            editBankAccount={editBankAccount}
                            deleteBankAccount={deleteBankAccount}
                            numerator={pageCountReceivedQuick}
                            denominator={pageCountExpectedQuick}
                            reassignBankAccount={reassignBankAccount}
                            loadingConnectedAccounts={loadingAccountsQuick}
                            loadingConnectedAccountsSlow={loadingAccountsSlow}
                            entities={entities}
                            assignEntityToBankAccounts={assignEntityToBankAccounts}
                            unassignEntityFromBankAccounts={unassignEntityFromBankAccounts}
                        />
                    },
                    {
                        "title": "Bank Exposure",
                        "key": 1,
                        "body": <BankExposure
                            bankAccounts={connectedAccounts}
                            loadingConnectedAccounts={loadingAccountsQuick}
                            tableData={institutions}
                            loadingConnectedAccountsSlow={loadingAccountsSlow}
                            institutions={institutions}
                        />
                    },
                    {
                        "title": "Closed Accounts",
                        "key": 2,
                        "body": <ClosedAccounts
                            closedAccounts={closedAccounts}
                            loadingClosedAccounts={loadingAccountsQuick}
                            setClosedAccounts={setClosedAccounts}
                            loadingConnectedAccountsSlow={loadingAccountsSlow}
                            setConnectedAccounts={setConnectedAccounts}
                        />
                    }
                ]}
            />

        </OuterBox>
    );
}

export default MyAccountsIndex